import type { ThemeDoc } from '../../../theme/types';

/**
 * Built-in presets.
 *
 * Deliberately only two, and neither is a design opinion.
 *
 * A previous attempt shipped six designer palettes (Warm Paper, Cool Slate, Dim
 * and friends) and was rejected outright - the direction, not the execution. The
 * lesson taken from that is that the app should ship the MECHANISM for presets
 * and let people build their own from the swatch palette, rather than handing
 * them somebody else's taste under an official-looking name.
 *
 * So the built-ins here are both things the app already is:
 *
 *   Stock          - no overrides at all. A named way back to the default look,
 *                    which doubles as the reset a user reaches for after
 *                    experimenting.
 *   Neutral Dark   - the near-black ramp that already ships as the "Grey" dark
 *                    palette in Settings. Expressing it as a preset is what
 *                    makes it reachable per-user and publishable org-wide,
 *                    instead of living only behind a separate toggle.
 */

export interface BuiltInPreset {
  slug: string;
  name: string;
  description: string;
  doc: ThemeDoc;
}

/**
 * The `.dark.theme-grey` ramp from index.css, transcribed exactly.
 *
 * Kept in sync by presets.test.ts, which parses the stylesheet and compares -
 * a preset that drifts from the palette it claims to be is worse than no preset,
 * because it looks official while quietly differing.
 */
const NEUTRAL_DARK_RAMP: Record<string, string> = {
  'c-gray-600': '75 75 75',
  'c-gray-700': '54 54 54',
  'c-gray-750': '46 46 46',
  'c-gray-800': '38 38 38',
  'c-gray-850': '31 31 31',
  'c-gray-900': '27 27 27',
  'c-gray-950': '15 15 15',
  'c-slate-600': '75 75 75',
  'c-slate-700': '54 54 54',
  'c-slate-750': '46 46 46',
  'c-slate-800': '38 38 38',
  'c-slate-850': '31 31 31',
  'c-slate-900': '27 27 27',
  'c-slate-950': '15 15 15',
};

export const BUILT_IN_PRESETS: BuiltInPreset[] = [
  {
    slug: 'stock',
    name: 'Stock',
    description: 'The default FastCMS look, with nothing overridden.',
    doc: { schemaVersion: 1, name: 'Stock' },
  },
  {
    slug: 'neutral-dark',
    name: 'Neutral Dark',
    description: 'The near-black dark palette, matching the Grey option in Settings.',
    doc: {
      schemaVersion: 1,
      name: 'Neutral Dark',
      // Light mode is deliberately untouched: this preset only has an opinion
      // about dark, exactly like the palette variant it mirrors.
      tokens: { dark: { ...NEUTRAL_DARK_RAMP } },
    },
  },
];

export const NEUTRAL_DARK_TOKENS = NEUTRAL_DARK_RAMP;

/**
 * Apply a preset onto the document being edited.
 *
 * A preset REPLACES the token maps rather than merging into them. Merging reads
 * as helpful and is not: picking a second preset would leave fragments of the
 * first behind, and there would be no way to get back to a clean one except by
 * resetting every field by hand. Typography and layout ride along the same way.
 *
 * The name is dropped: once applied it is the user's draft, not the preset, and
 * carrying the label would make a modified theme still claim to be "Stock".
 */
export function applyPreset(current: ThemeDoc, preset: ThemeDoc): ThemeDoc {
  return {
    schemaVersion: 1,
    // `enabled` belongs to the org document's lifecycle, not to a preset, so it
    // survives whatever the preset says.
    ...(current.enabled !== undefined ? { enabled: current.enabled } : {}),
    tokens: preset.tokens ? { ...preset.tokens } : undefined,
    components: preset.components ? { ...preset.components } : undefined,
    typography: preset.typography ? { ...preset.typography } : undefined,
    layout: preset.layout ? { ...preset.layout } : undefined,
  };
}
